import { supabase } from "@/integrations/supabase/client";

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

window.dataLayer = window.dataLayer || [];

// --- Attribution helpers ---------------------------------------------------
const SESSION_KEY_STORAGE = "mdcc_quiz_session_key";
const SESSION_ID_STORAGE = "mdcc_quiz_session_id";

const getSessionKey = (): string => {
  try {
    let k = sessionStorage.getItem(SESSION_KEY_STORAGE);
    if (!k) {
      k = (crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      sessionStorage.setItem(SESSION_KEY_STORAGE, k);
    }
    return k;
  } catch {
    return `${Date.now()}`;
  }
};

const getStoredSessionId = (): string | null => {
  try { return sessionStorage.getItem(SESSION_ID_STORAGE); } catch { return null; }
};
const setStoredSessionId = (id: string) => {
  try { sessionStorage.setItem(SESSION_ID_STORAGE, id); } catch { /* ignore */ }
};

const getAttribution = () => {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const get = (k: string) => params.get(k) || undefined;
  return {
    page_path: window.location.pathname + window.location.search,
    referrer: document.referrer || undefined,
    user_agent: navigator.userAgent,
    utm_source: get("utm_source"),
    utm_medium: get("utm_medium"),
    utm_campaign: get("utm_campaign"),
    utm_term: get("utm_term"),
    utm_content: get("utm_content"),
    gclid: get("gclid"),
    fbclid: get("fbclid"),
  };
};

const safe = <T>(p: PromiseLike<T> | T) => {
  void Promise.resolve(p).catch((e) => console.warn("[tracking] background task failed", e));
};

// --- GTM events -----------------------------------------------------------
export const trackEvent = (eventName: string, params?: Record<string, unknown>) => {
  window.dataLayer.push({
    event: eventName,
    ...params,
  });
  console.log('[GTM Event]', eventName, params);
};

export const trackQuizStep = (step: number, questionId: string, answer: string, question?: string) => {
  trackEvent('quiz_step_completed', {
    quiz_step: step,
    quiz_question: questionId,
    quiz_answer: answer,
  });
  const session_key = getSessionKey();
  safe((async () => {
    let session_id = getStoredSessionId();
    if (!session_id) {
      const { data, error } = await supabase
        .from("quiz_sessions")
        .insert({ session_key, ...getAttribution() })
        .select("id")
        .single();
      if (error) throw error;
      session_id = data.id;
      setStoredSessionId(session_id);
    }
    await supabase.from("quiz_step_answers").insert({
      session_id,
      session_key,
      step_number: step,
      step_name: questionId,
      question: question ?? questionId,
      answer,
    });
  })());
};

export const trackQuizStart = () => {
  trackEvent('quiz_started');
  const session_key = getSessionKey();
  if (getStoredSessionId()) return;
  safe((async () => {
    const { data, error } = await supabase
      .from("quiz_sessions")
      .insert({ session_key, ...getAttribution() })
      .select("id")
      .single();
    if (error) throw error;
    setStoredSessionId(data.id);
  })());
};

export const trackFormSubmission = (formData: {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  zipCode: string;
  service: string;
  timeline: string;
  homeType: string;
  budget: string;
}) => {
  trackEvent('form_submission', {
    form_type: 'quiz_lead',
    service_interest: formData.service,
    project_timeline: formData.timeline,
    home_type: formData.homeType,
    budget: formData.budget,
    zip_code: formData.zipCode,
  });

  trackEvent('user_data_ready', {
    user_first_name: formData.firstName,
    user_last_name: formData.lastName,
    user_email: formData.email,
    user_phone: formData.phone,
    user_zip_code: formData.zipCode,
  });

  trackEvent('generate_lead', {
    currency: 'USD',
    value: 1,
    user_first_name: formData.firstName,
    user_last_name: formData.lastName,
    user_email: formData.email,
    user_phone: formData.phone,
    user_zip_code: formData.zipCode,
  });

  const full_name = `${formData.firstName} ${formData.lastName}`.trim();
  const session_key = getSessionKey();
  const attr = getAttribution();

  // Update quiz session if one exists
  safe((async () => {
    const session_id = getStoredSessionId();
    const payload = {
      session_key,
      full_name,
      email: formData.email,
      phone: formData.phone,
      zip_code: formData.zipCode,
      service: formData.service,
      timeline: formData.timeline,
      home_type: formData.homeType,
      budget: formData.budget,
      completed: true,
      completed_at: new Date().toISOString(),
      ...attr,
    };
    if (session_id) {
      await supabase.rpc("update_quiz_session" as never, {
        p_session_id: session_id,
        p_session_key: session_key,
        p_full_name: full_name,
        p_email: formData.email,
        p_phone: formData.phone,
        p_zip_code: formData.zipCode,
        p_service: formData.service,
        p_timeline: formData.timeline,
        p_home_type: formData.homeType,
        p_budget: formData.budget,
        p_completed: true,
        p_completed_at: new Date().toISOString(),
        p_page_path: attr.page_path,
        p_referrer: attr.referrer,
        p_utm_source: attr.utm_source,
        p_utm_medium: attr.utm_medium,
        p_utm_campaign: attr.utm_campaign,
        p_utm_term: attr.utm_term,
        p_utm_content: attr.utm_content,
        p_gclid: attr.gclid,
        p_fbclid: attr.fbclid,
        p_user_agent: attr.user_agent,
      } as never);
    } else {
      const { data, error } = await supabase
        .from("quiz_sessions")
        .insert(payload as never)
        .select("id")
        .single();
      if (error) throw error;
      setStoredSessionId(data.id);
    }
  })());

  // Always also log a generic form_submissions row
  safe(supabase.from("form_submissions").insert({
    form_name: "quiz_lead",
    full_name,
    email: formData.email,
    phone: formData.phone,
    zip_code: formData.zipCode,
    service: formData.service,
    message: [formData.timeline, formData.homeType, formData.budget].filter(Boolean).join(" | "),
    ...attr,
  }) as unknown as Promise<unknown>);

  safe(supabase.functions.invoke("send-lead-notification", {
    body: {
      formType: "Quiz Lead",
      fields: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        zipCode: formData.zipCode,
        service: formData.service,
        timeline: formData.timeline,
        homeType: formData.homeType,
        budget: formData.budget,
        page: attr.page_path,
        source: attr.utm_source,
      },
    },
  }) as unknown as Promise<unknown>);
};

export const trackFreeEstimateForm = (data: {
  fullName: string;
  email: string;
  phone: string;
  zip: string;
  city?: string;
  state?: string;
  address?: string;
  service: string;
  source?: string;
  notes?: string;
  formName?: string;
}) => {
  const [firstName, ...rest] = (data.fullName || "").trim().split(/\s+/);
  const lastName = rest.join(" ");
  trackEvent("form_submission", { form_type: data.formName || "free_estimate", service_interest: data.service });
  trackEvent("user_data_ready", {
    user_first_name: firstName || "",
    user_last_name: lastName || "",
    user_email: data.email,
    user_phone: data.phone,
    user_zip_code: data.zip,
  });
  trackEvent("generate_lead", {
    currency: "USD",
    value: 1,
    user_first_name: firstName || "",
    user_last_name: lastName || "",
    user_email: data.email,
    user_phone: data.phone,
    user_zip_code: data.zip,
  });
  safe(supabase.from("form_submissions").insert({
    form_name: data.formName || "free_estimate",
    full_name: data.fullName,
    email: data.email,
    phone: data.phone,
    zip_code: data.zip,
    city: data.city,
    state: data.state,
    address: data.address,
    service: data.service,
    message: data.notes,
    source: data.source,
    ...getAttribution(),
  }) as unknown as Promise<unknown>);

  safe(supabase.functions.invoke("send-lead-notification", {
    body: {
      formType: data.formName || "Free Estimate",
      fields: {
        fullName: data.fullName,
        email: data.email,
        phone: data.phone,
        zip: data.zip,
        city: data.city,
        state: data.state,
        address: data.address,
        service: data.service,
        source: data.source,
        notes: data.notes,
      },
    },
  }) as unknown as Promise<unknown>);
};

export const trackPhoneClick = () => {
  const click_id = (crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);
  trackEvent('phone_call_click', {
    phone_number: '2405872207',
    click_id,
  });
  safe(supabase.from("call_clicks").insert({
    click_id,
    phone: "2405872207",
    ...getAttribution(),
  }) as unknown as Promise<unknown>);
};
